/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import user from 'js/lib/user';

import { Breadcrumbs, Grid, Link, PageGridContainer, Typography2, useTheme } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import useHasMultipleProgramProps from 'bundles/program-home/components/multiprogram/useHasMultipleProgramProps';

import _t from 'i18n!nls/program-home';

type PropsFromCaller = {
  programSlug: string;
  thirdPartyOrgSlug: string;
  thirdPartyOrganizationId: string;
  programName: string;
};

type Props = PropsFromCaller;

type PropsForHomeLink = {
  thirdPartyOrgSlug: string;
};

export const useStyles = () => {
  const theme = useTheme();
  return {
    container: css`
      background-color: ${theme.palette.gray[100]};
    `,
    breadcrumbs: css`
      padding: 16px 0 !important; // override default Breadcrumbs padding

      .cds-breadcrumbs-list {
        align-items: center;
      }

      ${theme.breakpoints.down('md')} {
        /* width: 1110px; */
        margin-left: auto;
        margin-right: auto;
        padding: ${theme.spacing(16, 24)};
      }
    `,
    itemText: css`
      font-weight: 700;
    `,
  };
};

const MultiProgramHomeLink = ({ thirdPartyOrgSlug, ...breadcrumbItemProps }: PropsForHomeLink) => {
  const styles = useStyles();
  return (
    <Breadcrumbs.Item {...breadcrumbItemProps}>
      <Link
        variant="quiet"
        trackingName="program_home_search_home_breadcrumb_link"
        data={{ thirdPartyOrgSlug }}
        component={TrackedLink2}
        href={`/programs/all/${thirdPartyOrgSlug}`}
        css={styles.itemText}
      >
        {_t('All Learning Programs')}
      </Link>
    </Breadcrumbs.Item>
  );
};

export const SearchBreadcrumbs = ({
  programSlug,
  programName,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
}: Props): JSX.Element | null => {
  const styles = useStyles();
  const { hasMultipleProgramsInOrg } = useHasMultipleProgramProps({ userId: user.get().id, thirdPartyOrganizationId });
  const homeUrl = hasMultipleProgramsInOrg ? `/programs/all/${thirdPartyOrgSlug}` : `/programs/${programSlug}`;
  return (
    <Typography2 component="div" css={styles.container}>
      <PageGridContainer>
        <Grid item xs={12}>
          <Breadcrumbs css={styles.breadcrumbs}>
            <Breadcrumbs.Item href={homeUrl}>{_t('Home')}</Breadcrumbs.Item>
            {hasMultipleProgramsInOrg && <MultiProgramHomeLink thirdPartyOrgSlug={thirdPartyOrgSlug} />}
            <Breadcrumbs.Item>
              <Link
                variant="quiet"
                trackingName="program_home_search_program_breadcrumb_link"
                data={{ programSlug }}
                component={TrackedLink2}
                href={`/programs/${programSlug}`}
                css={styles.itemText}
              >
                {programName}
              </Link>
            </Breadcrumbs.Item>
            <Breadcrumbs.Item css={styles.itemText}>{_t('Search')}</Breadcrumbs.Item>
          </Breadcrumbs>
        </Grid>
      </PageGridContainer>
    </Typography2>
  );
};

export default SearchBreadcrumbs;
